'use client';

import { RelatedInfo } from '@/app/lib/generated/graphql-types';
import { useCallback } from 'react';
import LoadingSpinner from '@/app/__to_be_integrated/_____components/_NEW/LoadingSpinner';
import EditMediaButtonOverlay from '@/app/features/shared/EditMediaButtonOverlay';
import DefaultHeroIntroContentEditable from '@/app/features/shared/intros/hero/ui/editable/DefaultHeroIntroContent.editable';
import EditableTypographyV2 from '@/app/features/shared/editable-typography/EditableTypographyV2';
import RelatedInfosView from '@/app/features/shared/related-infos/ui/RelatedInfos.public';
import { useSiteShopEditor } from '@/app/features/sites/shop/contexts/SiteShopEditorContext';
import { ShopProductPage } from '@/app/features/sites/shop/data-access/queries';
import ShopHero from '@/app/features/sites/shop/ui/shared/ShopHero';
import {
    ShopProductsFrame,
    ShopRootFrame,
} from '@/app/features/sites/shop/ui/shared/ShopFrames';
import ShopProducts from '@/app/features/sites/shop/ui/shared/ShopProducts.client';
import EditorHint from '@/app/features/shared/editor-hint/EditorHint';

export default function ShopEditable({
    products,
}: {
    products: {
        highlights: ShopProductPage;
        publications: ShopProductPage;
        other: ShopProductPage;
    };
}) {
    const { editable, updateEditable, errorForField } = useSiteShopEditor();

    const updateTitle = useCallback(
        (title: string) => updateEditable({ intro: { title } }),
        [updateEditable]
    );
    const updateSubtitle = useCallback(
        (subtitle: string) => updateEditable({ intro: { subtitle } }),
        [updateEditable]
    );
    const updateDescription = useCallback(
        (description: string | undefined) => updateEditable({ intro: { description } }),
        [updateEditable]
    );

    if (!editable) return <LoadingSpinner />;

    return (
        <ShopRootFrame>
            <ShopHero
                intro={editable.intro}
                backgroundOverlay={(
                    <EditMediaButtonOverlay
                        align="right"
                        onImageChange={(image) => {
                            if (image) updateEditable({ intro: { image } });
                        }}
                    />
                )}
                content={(
                    <DefaultHeroIntroContentEditable
                        data={editable.intro}
                        onTitleChange={updateTitle}
                        onSubtitleChange={updateSubtitle}
                        onDescriptionChange={updateDescription}
                        errorForField={errorForField}
                    />
                )}
            />
            <ShopProductsFrame>
                <EditorHint
                    description="Die Produkte werden im Admin-Dashboard gepflegt."
                    route={{
                        url: '/admin/shop/products',
                        label: 'Produkte bearbeiten',
                    }}
                >
                    <ShopProducts
                        {...products}
                        companion={(
                            <EditableTypographyV2
                                label="Einleitung zu den museumseigenen Publikationen"
                                component="p"
                                variant="subtitle1"
                                value={editable.companion ?? ''}
                                onChange={(companion) => updateEditable({ companion })}
                                error={Boolean(errorForField?.('companion'))}
                                helperText={errorForField?.('companion') || undefined}
                                align="center"
                            />
                        )}
                    />
                </EditorHint>
            </ShopProductsFrame>
            {editable.relatedInfos.length > 0 && (
                <RelatedInfosView relatedInfos={editable.relatedInfos as RelatedInfo[]} />
            )}
        </ShopRootFrame>
    );
}
